import React, { useState, useEffect } from "react";
import "./Dashboard.css";

export default function Dashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem("dashboard_auth");
    if (auth === "authenticated") {
      setIsAuthenticated(true);
      fetchAnalytics();
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    // Password:  (you can change this in the code)
    const correctPassword = "Hedia@@";
    
    if (password === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem("dashboard_auth", "authenticated");
      setError("");
      fetchAnalytics();
    } else {
      setError("❌ Incorrect password");
      setPassword("");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("dashboard_auth");
    setData(null);
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch("/api/analytics");
      const result = await response.json();
      
      // If no data, show a helpful message instead of zeros
      if (result.summary?.metrics.totalVisits === 0) {
        setError("📊 Analytics data is being collected. Data will appear after more visits due to serverless architecture limitations.");
      }
      
      setData(result);
    } catch (err) {
      setError("Failed to load analytics");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const triggerAIAnalysis = async () => {
    try {
      setAnalyzing(true);
      setError("");
      const response = await fetch("/api/analytics", { method: "POST" });
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError("AI analysis failed");
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  const triggerDailyReport = async () => {
    try {
      setAnalyzing(true);
      setError("");
      const response = await fetch("/api/daily-report", { method: "POST" });
      const result = await response.json();
      if (result.ok) {
        alert("✅ Daily report sent to Telegram!");
        fetchAnalytics();
      } else {
        alert("❌ " + (result.error || "Failed to send report"));
      }
    } catch (err) {
      setError("Failed to send report");
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="dashboard-container">
        <div className="login-box">
          <div className="login-header">
            <h1>🔒 Dashboard Login</h1>
            <p>Enter password to access analytics</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
              autoFocus
            />
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-btn">
              Unlock Dashboard
            </button>
          </form>
          <div className="login-footer">
            <p>Protected route • Authorized access only</p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        <div className="dashboard-header-bar">
          <div>
            <h1>📊 Smart Analytics Dashboard</h1>
            <p>AI-Powered Insights for Your Portfolio</p>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>

        {error && (
          <div className="dashboard-error">
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <div className="dashboard-loading">
            <div className="spinner"></div>
            <p>Loading analytics...</p>
          </div>
        ) : data ? (
          <div className="dashboard-content">
            {/* Summary Metrics */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <div className="metric-value">{data.summary?.metrics.totalVisits || 0}</div>
                <div className="metric-label">Total Visits</div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🆕</div>
                <div className="metric-value">{data.summary?.metrics.uniqueVisitors || 0}</div>
                <div className="metric-label">Unique Visitors</div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">�</div>
                <div className="metric-value">{data.summary?.metrics.totalContacts || 0}</div>
                <div className="metric-label">Contact Messages</div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🚫</div>
                <div className="metric-value">{data.summary?.metrics.spamBlocked || 0}</div>
                <div className="metric-label">Spam Blocked</div>
              </div>
            </div>

            <div className="dashboard-grid">
              {/* Top Pages */}
              {data.summary?.topPages && data.summary.topPages.length > 0 && (
                <div className="dashboard-card">
                  <h3>📄 Top Pages</h3>
                  <div className="top-list">
                    {data.summary.topPages.map((page, i) => (
                      <div key={i} className="top-item">
                        <span className="rank">{i + 1}</span>
                        <span className="name">{page.page}</span>
                        <span className="value">{page.views} views</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Projects */}
              {data.summary?.topProjects && data.summary.topProjects.length > 0 && (
                <div className="dashboard-card">
                  <h3>💼 Top Projects</h3>
                  <div className="top-list">
                    {data.summary.topProjects.map((project, i) => (
                      <div key={i} className="top-item">
                        <span className="rank">{i + 1}</span>
                        <span className="name">{project.project}</span>
                        <span className="value">{project.views} views</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Devices */}
              {data.summary?.devices && Object.keys(data.summary.devices).length > 0 && (
                <div className="dashboard-card full-width">
                  <h3>📱 Device Breakdown</h3>
                  <div className="device-list">
                    {Object.entries(data.summary.devices).map(([device, count], i) => (
                      <div key={i} className="device-item">
                        <span className="device-name">{device}</span>
                        <div className="device-bar">
                          <div
                            className="device-bar-fill"
                            style={{
                              width: `${(count / data.summary.metrics.totalVisits) * 100}%`,
                            }}
                          ></div>
                        </div>
                        <span className="device-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Contact Messages */}
              {data.summary?.contacts && data.summary.contacts.recent && data.summary.contacts.recent.length > 0 && (
                <div className="dashboard-card full-width">
                  <h3>📬 Recent Contact Messages</h3>
                  <div className="contacts-list">
                    {data.summary.contacts.recent.map((contact, i) => (
                      <div key={i} className={`contact-item ${contact.isSpam ? 'spam' : ''}`}>
                        <div className="contact-header-row">
                          <span className="contact-name">{contact.name}</span>
                          <span className={`contact-badge ${contact.priority.toLowerCase()}`}>
                            {contact.priority}
                          </span>
                          <span className="contact-category">{contact.category.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="contact-email">{contact.email}</div>
                        <div className="contact-message">{contact.message}</div>
                        <div className="contact-meta">
                          <span className="contact-sentiment">{contact.sentiment}</span>
                          {contact.isSpam && <span className="spam-badge">🚫 SPAM</span>}
                          <span className="contact-time">
                            {new Date(contact.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {data.aiInsights && (
                <div className="dashboard-card ai-insights full-width">
                  <h3>🤖 AI Insights</h3>
                  <div className="insights-content">
                    <pre>{data.aiInsights.insights}</pre>
                    <p className="insights-meta">
                      Based on {data.aiInsights.basedOnData.visits} visits •{" "}
                      {data.aiInsights.basedOnData.period}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="dashboard-actions">
              <button
                className="action-btn primary"
                onClick={triggerAIAnalysis}
                disabled={analyzing}
              >
                {analyzing ? "🤖 Analyzing..." : "🤖 Generate AI Insights"}
              </button>
              <button
                className="action-btn secondary"
                onClick={triggerDailyReport}
                disabled={analyzing}
              >
                {analyzing ? "📤 Sending..." : "📤 Send Daily Report"}
              </button>
              <button className="action-btn" onClick={fetchAnalytics}>
                🔄 Refresh Data
              </button>
            </div>
          </div>
        ) : (
          <div className="dashboard-empty">
            <p>No analytics data available yet.</p>
            <button onClick={fetchAnalytics}>Load Data</button>
          </div>
        )}
      </div>
    </div>
  );
}
